import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import BigNumber from 'bignumber.js';

/**
 * 데모거래 포지션 인터페이스
 */
export interface DemoPosition {
  id: string;
  symbol: string;
  side: 'LONG' | 'SHORT';
  entryPrice: string;
  quantity: string;
  leverage: number;
  marginType: 'isolated' | 'cross';
  unrealizedPnl: string;
  liquidationPrice: string;
  timestamp: number;
}

/**
 * 데모거래 주문 인터페이스
 */
export interface DemoOrder {
  id: string;
  symbol: string;
  side: 'BUY' | 'SELL';
  type: 'LIMIT' | 'MARKET';
  price: string;
  quantity: string;
  filledQuantity: string;
  status: 'NEW' | 'FILLED' | 'PARTIALLY_FILLED' | 'CANCELED';
  timestamp: number;
}

/**
 * 데모거래 계좌 정보
 */
export interface DemoAccountInfo {
  balance: string; // 총 잔액
  lockBalance: string; // 잠긴 잔액 (주문/포지션에 사용 중)
  availableBalance: string; // 사용 가능 잔액
  totalUnrealizedPnl: string; // 총 미실현 손익
  totalMargin: string; // 총 증거금
}

/**
 * 데모거래 스토어 상태
 */
interface DemoTradingState {
  isDemoMode: boolean; // 데모거래 모드 활성화 여부
  account: DemoAccountInfo;
  positions: DemoPosition[];
  orders: DemoOrder[];
  tradeHistory: Array<{
    id: string;
    symbol: string;
    side: 'BUY' | 'SELL';
    price: string;
    quantity: string;
    pnl: string;
    timestamp: number;
  }>;

  // Actions
  toggleDemoMode: () => void;
  setDemoMode: (enabled: boolean) => void;
  resetAccount: () => void;
  updateBalance: (amount: string) => void;
  addPosition: (position: DemoPosition) => void;
  closePosition: (positionId: string, exitPrice: string) => void;
  updatePositionPnl: (positionId: string, currentPrice: string) => void;
  addOrder: (order: DemoOrder) => void;
  updateOrder: (orderId: string, updates: Partial<DemoOrder>) => void;
  cancelOrder: (orderId: string) => void;
  clearAllPositions: () => void;
  clearAllOrders: () => void;
}

/**
 * 기본 계좌 정보 (초기 잔액 10,000 USDT)
 */
const DEFAULT_ACCOUNT: DemoAccountInfo = {
  balance: '10000',
  lockBalance: '0',
  availableBalance: '10000',
  totalUnrealizedPnl: '0',
  totalMargin: '0',
};

/**
 * 청산가 계산 함수
 * @param entryPrice - 진입가
 * @param side - 포지션 방향 (LONG/SHORT)
 * @param leverage - 레버리지
 * @param marginType - 마진 타입
 */
const calculateLiquidationPrice = (
  entryPrice: string,
  side: 'LONG' | 'SHORT',
  leverage: number,
  marginType: 'isolated' | 'cross'
): string => {
  const entry = new BigNumber(entryPrice);
  const maintenanceMarginRate = new BigNumber(0.004); // 0.4% (예시)

  if (marginType === 'isolated') {
    if (side === 'LONG') {
      // 롱 청산가 = 진입가 * (1 - 1/레버리지 + 유지증거금율)
      return entry
        .multipliedBy(
          new BigNumber(1)
            .minus(new BigNumber(1).dividedBy(leverage))
            .plus(maintenanceMarginRate)
        )
        .toFixed(2);
    } else {
      // 숏 청산가 = 진입가 * (1 + 1/레버리지 - 유지증거금율)
      return entry
        .multipliedBy(
          new BigNumber(1)
            .plus(new BigNumber(1).dividedBy(leverage))
            .minus(maintenanceMarginRate)
        )
        .toFixed(2);
    }
  }

  // Cross 모드는 더 복잡한 계산 필요 (단순화)
  return '0';
};

/**
 * 미실현 손익 계산 함수
 * @param entryPrice - 진입가
 * @param currentPrice - 현재가
 * @param quantity - 수량
 * @param side - 포지션 방향
 */
const calculateUnrealizedPnl = (
  entryPrice: string,
  currentPrice: string,
  quantity: string,
  side: 'LONG' | 'SHORT'
): string => {
  const entry = new BigNumber(entryPrice);
  const current = new BigNumber(currentPrice);
  const qty = new BigNumber(quantity);

  if (side === 'LONG') {
    // 롱 PnL = (현재가 - 진입가) * 수량
    return current.minus(entry).multipliedBy(qty).toFixed(2);
  } else {
    // 숏 PnL = (진입가 - 현재가) * 수량
    return entry.minus(current).multipliedBy(qty).toFixed(2);
  }
};

/**
 * 데모거래 Zustand 스토어
 * LocalStorage에 영속화하여 새로고침 시에도 데이터 유지
 */
export const useDemoTradingStore = create<DemoTradingState>()(
  persist(
    (set, get) => ({
      isDemoMode: false,
      account: { ...DEFAULT_ACCOUNT },
      positions: [],
      orders: [],
      tradeHistory: [],

      /**
       * 데모거래 모드 토글
       */
      toggleDemoMode: () => {
        set((state) => ({ isDemoMode: !state.isDemoMode }));
      },

      /**
       * 데모거래 모드 설정
       */
      setDemoMode: (enabled: boolean) => {
        set({ isDemoMode: enabled });
      },

      /**
       * 계좌 초기화 (잔액 리셋)
       */
      resetAccount: () => {
        set({
          account: { ...DEFAULT_ACCOUNT },
          positions: [],
          orders: [],
          tradeHistory: [],
        });
      },

      /**
       * 잔액 업데이트
       */
      updateBalance: (amount: string) => {
        set((state) => ({
          account: {
            ...state.account,
            balance: amount,
          },
        }));
      },

      /**
       * 포지션 추가
       */
      addPosition: (position: DemoPosition) => {
        set((state) => {
          const positions = [...state.positions, position];

          // 총 증거금 계산
          const totalMargin = positions.reduce((sum, pos) => {
            const positionValue = new BigNumber(pos.entryPrice)
              .multipliedBy(pos.quantity);
            const margin = positionValue.dividedBy(pos.leverage);
            return sum.plus(margin);
          }, new BigNumber(0));

          // 사용 가능 잔액 업데이트
          const availableBalance = new BigNumber(state.account.balance)
            .minus(totalMargin)
            .toFixed(2);

          return {
            positions,
            account: {
              ...state.account,
              totalMargin: totalMargin.toFixed(2),
              availableBalance,
            },
          };
        });
      },

      /**
       * 포지션 종료
       */
      closePosition: (positionId: string, exitPrice: string) => {
        set((state) => {
          const position = state.positions.find((p) => p.id === positionId);
          if (!position) return state;

          // 실현 손익 계산
          const pnl = calculateUnrealizedPnl(
            position.entryPrice,
            exitPrice,
            position.quantity,
            position.side
          );

          // 새 잔액 계산
          const newBalance = new BigNumber(state.account.balance)
            .plus(pnl)
            .toFixed(2);

          // 거래 기록 추가
          const tradeRecord = {
            id: `trade_${Date.now()}`,
            symbol: position.symbol,
            side: position.side === 'LONG' ? 'SELL' as const : 'BUY' as const,
            price: exitPrice,
            quantity: position.quantity,
            pnl,
            timestamp: Date.now(),
          };

          // 포지션 제거
          const positions = state.positions.filter((p) => p.id !== positionId);

          // 총 증거금 재계산
          const totalMargin = positions.reduce((sum, pos) => {
            const positionValue = new BigNumber(pos.entryPrice)
              .multipliedBy(pos.quantity);
            const margin = positionValue.dividedBy(pos.leverage);
            return sum.plus(margin);
          }, new BigNumber(0));

          const availableBalance = new BigNumber(newBalance)
            .minus(totalMargin)
            .toFixed(2);

          return {
            positions,
            tradeHistory: [tradeRecord, ...state.tradeHistory],
            account: {
              ...state.account,
              balance: newBalance,
              availableBalance,
              totalMargin: totalMargin.toFixed(2),
            },
          };
        });
      },

      /**
       * 포지션 미실현 손익 업데이트
       */
      updatePositionPnl: (positionId: string, currentPrice: string) => {
        set((state) => {
          const positions = state.positions.map((pos) => {
            if (pos.id === positionId) {
              const unrealizedPnl = calculateUnrealizedPnl(
                pos.entryPrice,
                currentPrice,
                pos.quantity,
                pos.side
              );
              return { ...pos, unrealizedPnl };
            }
            return pos;
          });

          // 총 미실현 손익 계산
          const totalUnrealizedPnl = positions
            .reduce((sum, pos) => sum.plus(pos.unrealizedPnl), new BigNumber(0))
            .toFixed(2);

          return {
            positions,
            account: {
              ...state.account,
              totalUnrealizedPnl,
            },
          };
        });
      },

      /**
       * 주문 추가
       */
      addOrder: (order: DemoOrder) => {
        set((state) => ({
          orders: [...state.orders, order],
        }));
      },

      /**
       * 주문 업데이트
       */
      updateOrder: (orderId: string, updates: Partial<DemoOrder>) => {
        set((state) => ({
          orders: state.orders.map((order) =>
            order.id === orderId ? { ...order, ...updates } : order
          ),
        }));
      },

      /**
       * 주문 취소
       */
      cancelOrder: (orderId: string) => {
        set((state) => ({
          orders: state.orders.map((order) =>
            order.id === orderId
              ? { ...order, status: 'CANCELED' as const }
              : order
          ),
        }));
      },

      /**
       * 모든 포지션 정리
       */
      clearAllPositions: () => {
        set({ positions: [] });
      },

      /**
       * 모든 주문 정리
       */
      clearAllOrders: () => {
        set({ orders: [] });
      },
    }),
    {
      name: 'demo-trading-storage',
      storage: createJSONStorage(() => localStorage),
      version: 1,
    }
  )
);

export default useDemoTradingStore;
